import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useResettablePagination } from '../../hooks/useResettablePagination';
import TableComponent from '../../components/TableComponent';
import TestGroupStatusBadge from './TestGroupStatusBadge';
import RowActionMenu from '../menu/RowActionMenu';
import { getTestGroups, deleteTestGroup, bulkDeleteTestGroups } from '../../utils/api/testgroup_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import mockTestGroups from './mockTestGroups';
import { PlusIcon, ClipboardDocumentListIcon, RocketLaunchIcon, PencilIcon } from '@heroicons/react/24/outline';
import PageHeader from '../Header/PageHeader';
import { useSelector, useDispatch } from 'react-redux';
import { updateColumnVisibility } from '../../features/filter/columnVisibilitySlice';
import CustomButton from "../Button/customButton/CustomButton";
import GetAppIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { Box, Stack, Typography, Chip, Tooltip, useTheme } from '@mui/material';
import { TrashIcon, FolderIcon, PlayIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useGridApiRef } from '@mui/x-data-grid';
import { updateUserPreferences } from '../../utils/api/userPreference_apis';
import { setActivePage } from '../../features/navigation/navigationSlice';
import { useNavigate } from 'react-router-dom';
import DEFAULT_ROUTES from '../../constants/routes';
import CreateTestGroupModal from './CreateTestGroupModal.jsx';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { useSnackbar } from '../../utils/useSnackbar';
import { exportToExcel } from '../../utils/exportToExcel';
import AddTestCasesToGroupModal from '../Modal/AddTestCasesToGroupModal';
import ExecuteTestGroupModal from '../Modal/ExecuteTestGroupModal';
import { ScaleEnum } from '../../enums/enumRegistry';
// Styles
import styles from '../../styles/component.module.scss';
import stylesTestGroupLibrary from '../../styles/testGroupLibrary/index.module.scss';
import ButtonComponent from '../customButton';
import TagsComponent from '../Tags';
import { renderTableCell } from '../../utils/commonfunctions.js';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import { getGroupTypeVariant } from '../chips/ChipComponent/chipVariants';
import CreateTestLaunchModal from '../Modal/CreateTestLaunchModal.jsx';
import QuickSearchInput from '../Query/QuickSearchInput';
import quickSearchBarStyles from '../../styles/quickSearchBar.module.scss';
import TestGroupEmptyScreen from './TestGroupEmptyScreen';
import {
  HOW_IT_WORKS_DOCS_PATH,
  getTestCasesRoute,
} from './testGroupEmptyScreen.constants';
import { formatStatusKey } from '../TestLaunchDetail/statusColorPallete';

const headerContent = (total) => (
  <div className={stylesTestGroupLibrary['test-group-library-title']}>
    <span className={stylesTestGroupLibrary['title-text']}>Test Group Library</span>
    <span className={stylesTestGroupLibrary['title-badge']}>
      {total} total
    </span>
  </div>
);

const USE_MOCK_DATA = false; // Set to true to use mock data for visual purposes

export default function TestGroupLibrary() {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const columnVisibilityModel = useSelector((state) => state.columnVisibility.columnVisibilityModel);
  const handleColumnVisibilityModelChange = async (newModel) => {
    Object.entries(newModel).forEach(([columnName, visibility]) => {
      dispatch(updateColumnVisibility({ columnName, visibility }));
    });
    // Persist to backend
    const response = await updateUserPreferences([
      {
        viewType: 'testGroup',
        preferences: { columnVisible: Object.keys(newModel).filter(key => newModel[key]) }
      }
    ]);
    
    if (response.status === STATUS.ERROR) {
      console.error('Failed to update user preferences:', response.error);
    }
  };
  const [loading, setLoading] = useState(true);
  const [testGroups, setTestGroups] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [quickSearchValue, setQuickSearchValue] = useState('');
  const [filterAst, setFilterAst] = useState(null);
  // Pagination state — snaps back to page 0 when the filter/search changes.
  const [paginationModel, setPaginationModel] = useResettablePagination({
    pageSize: 25,
    resetKey: JSON.stringify(filterAst),
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const { selectedProject } = useSelector((state) => state.projects);
  const activePage = useSelector((state) => state.navigation.activePage);
  const { showSnackbar } = useSnackbar();
  const breadcrumbList = [
    {
      label: "Project",
      onClick: () => handleBreadcrumbClick("home"),
      // icon: (<Tooltip title="Home"><Home /></Tooltip>),
    },
    {
      label: selectedProject?.name || "Unknown Project",
    },
    { 
      label: activePage 
    },
  ]
  const theme = useTheme();


  const columns = [
    {
        field: '__check__',
        hideable: false,
        minWidth: 50,
        maxWidth: 50,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
    },

  {
    field: 'key',
    headerName: 'Group ID',
    minWidth: 80,
    maxWidth: 110,
    renderCell: (params) => (
      renderTableCell(params.value, "link", {
        type: 'link',
        to: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}${DEFAULT_ROUTES.TEST_GROUPS}/${params.row.id}`,
      })
    )
  },
  {
    field: 'name',
    headerName: 'Group Name & Description',
    minWidth: 260,
    flex: 1,
    renderCell: (params) => (
      <div className={stylesTestGroupLibrary['group-name-description']}>
        <div className={`${stylesTestGroupLibrary['group-name']} ellipsis-container-line-2`} title={params.value}>{params.value}</div>
        <div
          className={`${stylesTestGroupLibrary['group-description']} ellipsis-container-line-2`}
          title={params.row.description}
        >
          {params.row.description}
        </div>
      </div>
    ),
  },
  {
    field: 'groupType',
    headerName: 'Group Type',
    minWidth: 90,
    maxWidth: 110,
    renderCell: (params) => (
      <ChipComponent
        variant={getGroupTypeVariant(params.value)}
        size="small"
      >
        {params.value || 'Unknown'}
      </ChipComponent>
    ),
  },
  {
    field: 'testCaseCount',
    headerName: 'Test Cases',
    minWidth: 100,
    maxWidth: 110,
    renderCell: (params) => (
      <span className={stylesTestGroupLibrary['test-case-count']}>
        <span className={stylesTestGroupLibrary['count-icon']}>
          <ClipboardDocumentListIcon className="icon text-blue-500" />
        </span>
        <span className={stylesTestGroupLibrary['count-text']}>{params.value}</span>
      </span>
    ),
  },
  {
    field: 'launchCount',
    headerName: 'Launch Count',
    minWidth: 100,
    maxWidth: 120,
    renderCell: (params) => (
      <span className={stylesTestGroupLibrary['launch-count']}>
        <span className={stylesTestGroupLibrary['count-icon']}>
          <RocketLaunchIcon className="icon text-green-500" />
        </span>
        <span className={stylesTestGroupLibrary['count-text']}>{params.value}</span>
      </span>
    ),
  },
  {
    field: 'scheduledToRun',
    headerName: 'Scheduled To Run',
    minWidth: 130,
    maxWidth: 150,
    renderCell: (params) => (
      <ChipComponent variant="default" size="small">
        {params.value || 'No'}
      </ChipComponent>
    ),
  },
  {
    field: 'executionStatus',
    headerName: 'Last Exec Status',
    minWidth: 190,
    maxWidth: 210,
    // flex: 1,
    renderCell: (params) => {
      return <TestGroupStatusBadge status={params.row.executionStatus || {}} />
    }
  },
  {
    field: 'lastRunDate',
    headerName: 'Last Launch Date',
    type: 'dateTime',
    minWidth: 140,
    maxWidth: 180,
    valueGetter: (value) => value && value !== '-' ? new Date(value) : null,
    renderCell: (params) => {
      if (!params.value) {
        return <span className={stylesTestGroupLibrary['no-date']}>-</span>;
      }
      const date = params.value.toLocaleDateString('en-IN');
      const time = params.value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      return (
        <div className={stylesTestGroupLibrary['date-cell']}>
          <span className={stylesTestGroupLibrary['date']}>{date}</span>
          <br />
          <span className={stylesTestGroupLibrary['time']}>{time}</span>
        </div>
      );
    },
  },
  {
    field: 'nextRunDate',
    headerName: 'Next Scheduled Run',
    type: 'dateTime',
    minWidth: 150,
    maxWidth: 170,
    valueGetter: (value) => value && value !== '-' ? new Date(value) : null,
    renderCell: (params) => {
      if (!params.value) {
        return <span className={stylesTestGroupLibrary['no-date']}>-</span>;
      }
      const date = params.value.toLocaleDateString('en-IN');
      const time = params.value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      return (
        <div className={stylesTestGroupLibrary['date-cell']}>
          <span className={stylesTestGroupLibrary['date']}>{date}</span>
          <br />
          <span className={stylesTestGroupLibrary['time']}>{time}</span>
        </div>
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    minWidth: 100,
    maxWidth: 120,
    // flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <RowActionMenu
        menuItems={rowActionButtons}
        rowData={params.row}
        isDisabled={false}
      />
    ),
  },
];



  function handleBreadcrumbClick(id) {
    let {rURL, activePage=null} = (() => {
      if (id == "home") {
        return {
          rURL: DEFAULT_ROUTES.BASE_URL,
          activePage: "Projects",
        };
      }
      if (id == "project") {
        return {
          rURL: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}/test-cases`,
          activePage: "Test Cases",
        };
      }
    })();
    if (activePage) {
      dispatch(setActivePage(activePage));
    }
    navigate(rURL);
  }


  const fetchGroups = async () => {
    setLoading(true);
    try {
      if (USE_MOCK_DATA) {
        setTestGroups(mockTestGroups);
        setTotalCount(mockTestGroups.length);
      } else {
        const response = await getTestGroups({
          page: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          ...(filterAst ? { filterAst } : {}),
        });
        
        if (response.status === STATUS.SUCCESS) {
          setTestGroups(response.data?.rows || []);
          setTotalCount(response.data?.total || 0);
        } else {
          setTestGroups([]);
          setTotalCount(0);
          showSnackbar(response.error || 'Failed to fetch test groups', 'error');
        }
      }
    } catch (error) {
      setTestGroups([]);
      setTotalCount(0);
      showSnackbar(error.message || 'Error fetching test groups', 'error');
    } finally {
      setLoading(false);
    }
  };

  
  useEffect(() => {
    if (!USE_MOCK_DATA && !selectedProject?.id) {
      setLoading(false);
      return;
    }
    fetchGroups();
    // eslint-disable-next-line
  }, [selectedProject?.id, paginationModel.page, paginationModel.pageSize, filterAst]);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  const handleModalSuccess = (createdGroup) => {
    // Navigate to the detail page if a group was created
    if (createdGroup && createdGroup.id && selectedProject?.id) {
      console.log('Navigating to test group detail:', createdGroup.id);
      
      // Validate that we have all required data
      if (!createdGroup.id || !selectedProject.id) {
        console.error('Missing required data for navigation:', { createdGroup, selectedProject });
        fetchGroups();
        return;
      }
      
      // Update the active page to "Test Groups" before navigating
      dispatch(setActivePage("Test Groups"));
      
      // Add a small delay to ensure the modal state is fully updated before navigation
      setTimeout(() => {
        try {
          const navigationPath = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}${DEFAULT_ROUTES.TEST_GROUPS}/${createdGroup.id}`;
          console.log('Navigating to:', navigationPath);
          navigate(navigationPath);
        } catch (error) {
          console.error('Navigation error:', error);
          // Fallback: just refresh the list if navigation fails
          fetchGroups();
        }
      }, 150);
    } else if (!selectedProject?.id) {
      console.error('No project selected, cannot navigate to test group detail');
      showSnackbar('No project selected. Please select a project first.', 'error');
      fetchGroups();
    } else {
      console.warn('Missing data for navigation:', { createdGroup, selectedProject });
      // Fallback: just refresh the list
      fetchGroups();
    }
  };

  const handleEditGroup = (group) => {
    console.log("group",group);
    setEditGroup(group);
    setEditModalOpen(true);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // single group or array of ids

  const handleDeleteGroup = (group) => {
    setDeleteTarget(group);
    setDeleteModalOpen(true);
  };

  const handleBulkDeleteGroups = () => {
    setDeleteTarget(selectedRows);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (Array.isArray(deleteTarget)) {
        const response = await bulkDeleteTestGroups({ ids: deleteTarget });
        if (response.status === STATUS.SUCCESS) {
          const snapbarText = (deleteTarget.length > 1) ? `${deleteTarget.length} Test group(s) deleted successfully` : 'Test group deleted successfully';
          showSnackbar(response.message || snapbarText, 'success');
        } else {
          showSnackbar(response.error || 'Failed to delete test groups', 'error');
        }
      } else if (deleteTarget && deleteTarget.id) {
        const response = await deleteTestGroup({ id: deleteTarget.id });
        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || 'Test group deleted successfully', 'success');
        } else {
          showSnackbar(response.error || 'Failed to delete test group', 'error');
        }
      }
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      setSelectedRows([]);
      fetchGroups();
    } catch (error) {
      showSnackbar('Failed to delete test group(s): ' + error.message, 'error');
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  // Helper function to format date for export
  const formatDateForExport = (dateValue) => {
    if (!dateValue || dateValue === '-' || typeof dateValue !== 'string') {
      return '-';
    }
    const d = new Date(dateValue);
    if (isNaN(d)) {
      return '-';
    }
    const date = d.toLocaleDateString('en-IN'); // DD/MM/YYYY
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }); // HH:mm:ss
    return `${date} ${time}`;
  };

  const formatExecutionStatusForExport = (status) => {
    // If it's a string, return as-is
    if (typeof status === 'string') {
      return status;
    }
    
    // If it's not an object or is null/undefined, return dash
    if (!status || typeof status !== 'object') {
      return '-';
    }
    
    const parts = [];
    
    // Dynamically handle all status keys except 'overall'
    Object.entries(status).forEach(([key, value]) => {
      // Skip 'overall' key as it will be handled separately
      if (key === 'overall') {
        return;
      }
      
      // Only add if value is greater than 0
      if (typeof value === 'number' && value > 0) {
        // Capitalize first letter of the key
        const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
        parts.push(`${value} ${capitalizedKey}`);
      }
    });
    
    // Add overall status at the end
    if (status.overall) {
      parts.push(`Overall: ${formatStatusKey(status.overall)}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const handleExportAll = () => {
    // Get sorted row IDs from DataGrid
    const sortedRowIds = apiRef.current?.getSortedRowIds() || [];
    
    // Create a map for quick lookup
    const groupMap = new Map(testGroups.map(g => [g.id, g]));
    
    // Get sorted groups based on the DataGrid's sorted order
    const sortedGroups = sortedRowIds
      .map(id => groupMap.get(id))
      .filter(Boolean);
    
    // If no sorted data, fallback to original order
    const dataToExport = sortedGroups.length > 0 ? sortedGroups : testGroups;
    
    const data = dataToExport.map(group => ({
      'Group ID': group.key,
      'Group Name': group.name,
      'Description': group.description,
      'Group Type': group.groupType,
      'Test Cases': group.testCaseCount,
      'Launch Count': group.launchCount,
      'Scheduled To Run': group.scheduledToRun,
      'Last Exec Status': formatExecutionStatusForExport(group.executionStatus),
      'Last Launch Date': formatDateForExport(group.lastRunDate),
      'Next Scheduled Run': formatDateForExport(group.nextRunDate),
    }));
    exportToExcel(data, 'TestGroups.xlsx', 'Test Groups');
  };

  const [addTestCasesModalOpen, setAddTestCasesModalOpen] = useState(false);
  const [targetGroupForAdd, setTargetGroupForAdd] = useState(null);
  const [executeModalOpen, setExecuteModalOpen] = useState(false);
  const [targetGroupForExecute, setTargetGroupForExecute] = useState(null);

  const handleAddTestCases = (group) => {
    setTargetGroupForAdd(group);
    setAddTestCasesModalOpen(true);
  };

  // Handler for executing a single test group
  const handleExecuteGroup = (group) => {
    setTargetGroupForExecute(group);
    setExecuteModalOpen(true);
  };

  // Handler for successful execution
  const handleExecuteSuccess = () => {
    setExecuteModalOpen(false);
    setTargetGroupForExecute(null);
    fetchGroups();
  };

  // Add row click handler for navigation to detail page
  const handleRowClick = (params) => {
    const group = params.row;
    if (group && selectedProject?.id && group.id) {
      navigate(`/projects/${selectedProject.id}/test-groups/${group.id}`);
    }
  };

  // Row action buttons for test group row
  const rowActionButtons = [
    {
      name: 'Edit',
      tooltip: 'Edit Group',
      icon: <PencilIcon className="icon" />,
      action: (row) => handleEditGroup(row),
      disabled: false,
    },
    {
      name: 'Add Test Cases',
      tooltip: 'Add Test Cases to Group',
      icon: <PlusIcon className="icon" />,
      action: (row) => handleAddTestCases(row),
      disabled: false,
    },
    {
      name: 'Execute',
      tooltip: 'Execute Test Group',
      icon: <PlayIcon className="icon" />,
      action: (row) => handleExecuteGroup(row),
      disabled: (row) => row.testCaseCount === 0,
    },
    {
      name: 'Delete',
      tooltip: 'Delete Group',
      icon: <TrashIcon className="icon" />,
      action: (row) => handleDeleteGroup(row),
      disabled: false,
    },
  ];

  // Move headerActionButtons here so it can access selectedRows
  const headerActionButtons = [
    {
      name: "Delete",
      tooltip: "Delete selected group(s)",
      icon: <TrashIcon className="icon" />, 
      action: handleBulkDeleteGroups,
      disabled: selectedRows.length === 0 || loading,
      section: 'left',
      contained: true,
    },
    // {
    //   name: "Execute Test Groups",
    //   tooltip: "Execute Selected Test Groups",
    //   icon: <PlayIcon className="icon" />,
    //   action: () => { /* execute logic */ },
    //   disabled: selectedRows.length === 0 || selectedRows.some(row => testGroups.find(tg => tg.id === row)?.testCaseCount === 0) || loading,
    //   section: 'left',
    //   contained: true,
    // }
  ];
  
  const onCloseLaunchModal = () => {
    setExecuteModalOpen(false);
    setTargetGroupForExecute(null);
  };

  const handleQuickSearchChange = useCallback((ast) => {
    setLoading(true);
    setFilterAst(ast ?? null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleCustomPaginationPageChange = useCallback((event, page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  }, []);

  const handleCustomPaginationPageSizeChange = useCallback((pageSize) => {
    setPaginationModel((prev) => ({ ...prev, page: 0, pageSize }));
  }, []);

  const tableCustomStyles = useMemo(
    () => ({
      '& .MuiDataGrid-cell': { py: 1 },
      '& .selected-row': {
        backgroundColor: theme.palette.customColor.blue[50],
        '&:hover': { backgroundColor: theme.palette.customColor.blue[100] },
      },
    }),
    [theme.palette.customColor?.blue?.[50], theme.palette.customColor?.blue?.[100]]
  );

  const headerContentRightElement = useMemo(
    () => (
      <Box className={quickSearchBarStyles.quickSearchBarWrap}>
        <QuickSearchInput
          entityType="testgroup"
          value={quickSearchValue}
          onChange={setQuickSearchValue}
          onSearchChange={handleQuickSearchChange}
          placeholder="Search test groups..."
        />
      </Box>
    ),
    [quickSearchValue, handleQuickSearchChange]
  );

  const headerContentRight = useCallback(() => headerContentRightElement, [headerContentRightElement]);

  const isSearchOrFilterActive = quickSearchValue.trim().length > 0 || Boolean(filterAst);
  const showTestGroupEmptyScreen = !loading
    && Boolean(selectedProject?.id)
    && totalCount === 0
    && !isSearchOrFilterActive;

  const handleHowItWorks = useCallback(() => {
    navigate(HOW_IT_WORKS_DOCS_PATH);
  }, [navigate]);

  const handleGoToTestCases = useCallback(() => {
    if (!selectedProject?.id) {
      return;
    }

    dispatch(setActivePage('Test Cases'));
    navigate(getTestCasesRoute(selectedProject.id));
  }, [dispatch, navigate, selectedProject?.id]);

  return (
    <>
      <div className={styles['page-container']}>
        <div>
          <PageHeader
            projectName={selectedProject?.name}
            title="Test Groups"
            description="Organize and manage collections of related test cases for efficient test execution and reporting"
            activePage={activePage}
            breadcrumbItems={breadcrumbList}
            actions={[
              <ButtonComponent
                variant="outline"
                className={`${stylesTestGroupLibrary['action-button']} ${stylesTestGroupLibrary['outlined']}`}
                onClick={handleExportAll}
                ctaText="Export All"
                startIcon={<ArrowDownTrayIcon className="icon" />}
                disabled={totalCount === 0 || loading}
              />,

              <ButtonComponent
                className={`${stylesTestGroupLibrary['action-button']} ${stylesTestGroupLibrary['primary']}`}
                variant="primary"
                startIcon={<AddIcon className="icon" />}
                onClick={handleOpenModal}
                ctaText="New Test Group"
              />
            ]}
          />
        </div>

      <Box className={styles['cover-container-col']} sx={{ width: '100%', paddingX: 2, paddingTop: 0 }}>
        <TableComponent
          viewType="testGroup"
          title="Test Groups Library"
          headerActionButtons={headerActionButtons}
          headerContentRight={headerContentRight}
          columns={columns}
          rows={testGroups}
          isCheckbox={true}
          isRowSelection={true}
          loading={loading}
          idField="id"
          isColumnResizable={!loading}
          isPagination={true}
          selectedRows={selectedRows}
          handleRowSelection={setSelectedRows}
          rowCount={totalCount}
          paginationModel={paginationModel}
          paginationMode="server"
          handleCustomPaginationPageChange={handleCustomPaginationPageChange}
          handleCustomPaginationPageSizeChange={handleCustomPaginationPageSizeChange}
          isCustomPagination={true}
          getRowHeight={() => 'auto'}
          apiRef={apiRef}
          getEstimatedRowHeight={() => 100}
          customStyles={tableCustomStyles}
          isData={!showTestGroupEmptyScreen}
          // isVisibleInsideTableNoData={showTestGroupEmptyScreen}
          customNoData={(
            <TestGroupEmptyScreen
              onCreateTestGroup={handleOpenModal}
              onHowItWorks={handleHowItWorks}
              onGoToTestCases={handleGoToTestCases}
            />
          )}
        />
      </Box>
    </div>
      {/* Modal for creating test group */}
      <CreateTestGroupModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        projectId={selectedProject?.id}
        existingTestGroups={testGroups}
      />

      {/* Modal for editing test group */}
      <CreateTestGroupModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onEdit={fetchGroups}
        editMode={true}
        initialValues={editGroup ? {
          ...editGroup,
        } : {}}
        projectId={selectedProject?.id}
        existingTestGroups={testGroups}
      />

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={getDeleteModalTitle()}
        message={getDeleteConfirmMessage(
          'test group',
          Array.isArray(deleteTarget) ? deleteTarget.length : undefined,
          Array.isArray(deleteTarget),
          'This will also permanently delete all associated test launches and their details.'
        )}
      />

      {/* Add Test Cases to Group Modal */}
      <AddTestCasesToGroupModal
        open={addTestCasesModalOpen}
        onClose={() => setAddTestCasesModalOpen(false)}
        testGroup={targetGroupForAdd}
        onSuccess={(isCloseModel = false) => {
          if (isCloseModel) {
            setAddTestCasesModalOpen(false);
            setTargetGroupForAdd(null);
          }
          fetchGroups();
          showSnackbar('Test cases added to group successfully', 'success');
        }}
      />

      {/* Execute Test Group Modal */}
      {/* <ExecuteTestGroupModal
        open={executeModalOpen}
        onClose={() => {
          setExecuteModalOpen(false);
          setTargetGroupForExecute(null);
        }}
        onSuccess={handleExecuteSuccess}
        group={targetGroupForExecute}
        projectId={selectedProject?.id}
      /> */}

      <CreateTestLaunchModal
        open={executeModalOpen}
        onClose={onCloseLaunchModal}
        onSubmit={handleExecuteSuccess}
        group={targetGroupForExecute}
        projectId={selectedProject?.project_id}
      />
    </>
  );
} 